import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { API } from '@/lib/catalog';

const EMPTY = { name: '', email: '', phone: '', comment: '' };
const inputCls =
  'w-full rounded-[32px] border border-candy-ink-muted/60 bg-candy-surface px-7 text-[17px] text-candy-ink placeholder:text-candy-ink-muted focus:border-candy-ink focus:outline-none';

export const ContactPage = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = t('meta.infoTitle', { name: t('info.contact.title') });
  }, [t]);

  const set = (field) => (e) => setForm((cur) => ({ ...cur, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setForm(EMPTY);
      setSent(true);
    } catch {
      toast.error(t('info.contact.failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto max-w-[760px] px-4 pb-16 pt-8 md:px-6 md:pt-14" data-testid="contact-page">
      <h1 className="font-heading text-[40px] font-extrabold leading-none tracking-[-0.035em] md:text-[56px]">{t('info.contact.title')}</h1>
      <p className="mt-5 text-[17px] text-candy-ink-muted">{t('info.contact.intro')}</p>

      {sent ? (
        <div className="mt-8 rounded-block bg-candy-pink p-7" role="status" data-testid="contact-sent">
          <p className="font-heading text-[22px] font-extrabold">{t('info.contact.sentTitle')}</p>
          <p className="mt-2">{t('info.contact.sentText')}</p>
          <button type="button" onClick={() => setSent(false)} className="mt-4 font-bold underline underline-offset-2">
            {t('info.contact.another')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5" data-testid="contact-form">
          <label className="sr-only" htmlFor="contact-name">{t('info.contact.name')}</label>
          <input id="contact-name" autoComplete="name" maxLength={200} value={form.name} onChange={set('name')} placeholder={t('info.contact.name')} className={`${inputCls} h-[64px]`} />

          <label className="sr-only" htmlFor="contact-email">{t('info.contact.email')}</label>
          <input id="contact-email" type="email" required autoComplete="email" value={form.email} onChange={set('email')} placeholder={`${t('info.contact.email')} *`} className={`${inputCls} h-[64px]`} />

          <label className="sr-only" htmlFor="contact-phone">{t('info.contact.phone')}</label>
          <input id="contact-phone" type="tel" autoComplete="tel" maxLength={50} value={form.phone} onChange={set('phone')} placeholder={t('info.contact.phone')} className={`${inputCls} h-[64px]`} />

          <label className="sr-only" htmlFor="contact-comment">{t('info.contact.comment')}</label>
          <textarea id="contact-comment" rows={5} maxLength={5000} value={form.comment} onChange={set('comment')} placeholder={t('info.contact.comment')} className={`${inputCls} resize-y py-5`} />

          <button
            type="submit"
            disabled={sending}
            className="mt-3 h-[64px] w-full max-w-[200px] rounded-full bg-accent text-[17px] font-bold tracking-wide text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            data-testid="contact-submit"
          >
            {sending ? t('info.contact.sending') : t('info.contact.send')}
          </button>
        </form>
      )}
    </main>
  );
};
